import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import httpRequest from '../../ajax';
import { queryKeys } from '../../api/queryKeys';

/**
 * Community feed TanStack Query hooks.
 *
 * Replaces the Redux-based feedAction.js / feedReducer.js flow with
 * TanStack Query for caching, optimistic updates, and automatic refetch.
 *
 * API endpoints (CommunityController):
 *   GET    /community/posts?page=N         — list posts
 *   POST   /community/posts                — create post
 *   DELETE /community/posts/{id}           — delete post
 *   POST   /community/posts/{id}/like      — like
 *   DELETE /community/posts/{id}/like      — unlike
 *   GET    /community/posts/{id}/comments  — list comments
 *   POST   /community/posts/{id}/comments  — add comment
 *   DELETE /community/posts/{id}/comments/{cid} — delete comment
 */

// ───────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────

/**
 * useCommunityPosts — fetches the paginated community feed.
 * Data is cached for 1 minute; refetches on mount.
 */
export function useCommunityPosts(page = 1) {
  return useQuery({
    queryKey: queryKeys.communityPosts(page),
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', `/community/posts?page=${page}`);
      // API may return an array or { posts: [...] }
      if (Array.isArray(data)) return data;
      if (data?.posts) return data.posts;
      if (data?.data) return data.data;
      return [];
    },
    staleTime: 60 * 1000,
    refetchOnMount: true,
    keepPreviousData: true,
  });
}

/**
 * usePostComments — lazily fetches comments for a single post.
 * Only enabled when the comments panel is expanded (pass enabled=true).
 */
export function usePostComments(postId, enabled = false) {
  return useQuery({
    queryKey: queryKeys.postComments(postId),
    queryFn: async () => {
      const { data: { data } } = await httpRequest('get', `/community/posts/${postId}/comments`);
      return data || [];
    },
    enabled: Boolean(postId) && enabled,
    staleTime: 30 * 1000,
  });
}

// ───────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────

/**
 * useCreatePost — creates a new community post.
 * Optimistically prepends the post to the cache.
 */
export function useCreatePost(page = 1) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.communityPosts(page);

  return useMutation({
    mutationFn: async ({ body, image_urls }) => {
      const { data: { data } } = await httpRequest('post', '/community/posts', {
        body,
        image_urls: image_urls || [],
      });
      return data;
    },
    onMutate: async (newPost) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      // Optimistic placeholder
      const optimisticPost = {
        id: `temp-${Date.now()}`,
        body: newPost.body,
        image_urls: newPost.image_urls || [],
        author_name: 'You',
        author_avatar: null,
        created_at: 'just now',
        like_count: 0,
        comment_count: 0,
        liked_by_me: false,
        comments: [],
        _optimistic: true,
      };
      queryClient.setQueryData(queryKey, (old = []) => [optimisticPost, ...old]);
      return { previous };
    },
    onError: (_err, _newPost, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: (data, _vars, context) => {
      // Replace optimistic placeholder with real data
      queryClient.setQueryData(queryKey, (old = []) =>
        old.map((p) => (p._optimistic ? { ...data, comments: [] } : p))
      );
    },
  });
}

/**
 * useDeletePost — deletes a community post.
 * Optimistically removes it from the cache.
 */
export function useDeletePost(page = 1) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.communityPosts(page);

  return useMutation({
    mutationFn: async (postId) => {
      await httpRequest('delete', `/community/posts/${postId}`);
      return postId;
    },
    onMutate: async (postId) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old = []) =>
        old.filter((p) => String(p.id) !== String(postId))
      );
      return { previous };
    },
    onError: (_err, _postId, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
  });
}

/**
 * useToggleLike — likes or unlikes a post.
 * Optimistically updates the like count and liked_by_me flag.
 */
export function useToggleLike(page = 1) {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.communityPosts(page);

  return useMutation({
    mutationFn: async ({ postId, likedByMe }) => {
      const method = likedByMe ? 'delete' : 'post';
      const { data: { data } } = await httpRequest(method, `/community/posts/${postId}/like`);
      return { postId, like_count: data?.like_count, liked_by_me: data?.liked_by_me };
    },
    onMutate: async ({ postId, likedByMe }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old = []) =>
        old.map((p) =>
          String(p.id) === String(postId)
            ? {
                ...p,
                liked_by_me: !likedByMe,
                like_count: Math.max(0, (p.like_count || 0) + (likedByMe ? -1 : 1)),
              }
            : p
        )
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: ({ postId, like_count, liked_by_me }) => {
      // Set the authoritative values from the server
      queryClient.setQueryData(queryKey, (old = []) =>
        old.map((p) =>
          String(p.id) === String(postId)
            ? { ...p, like_count, liked_by_me }
            : p
        )
      );
    },
  });
}

/**
 * useCreateComment — adds a comment to a post.
 * Optimistically appends to the comment list and bumps comment_count.
 */
export function useCreateComment(page = 1) {
  const queryClient = useQueryClient();
  const postsKey = queryKeys.communityPosts(page);

  return useMutation({
    mutationFn: async ({ postId, body }) => {
      const { data: { data } } = await httpRequest('post', `/community/posts/${postId}/comments`, { body });
      return { postId, comment: data };
    },
    onMutate: async ({ postId, body }) => {
      await queryClient.cancelQueries({ queryKey: postsKey });
      const previous = queryClient.getQueryData(postsKey);
      const optimisticComment = {
        id: `temp-c-${Date.now()}`,
        body,
        author_name: 'You',
        avatar: null,
        created_at: 'just now',
        _optimistic: true,
      };
      queryClient.setQueryData(postsKey, (old = []) =>
        old.map((p) =>
          String(p.id) === String(postId)
            ? {
                ...p,
                comments: [...(p.comments || []), optimisticComment],
                comment_count: (p.comment_count || 0) + 1,
              }
            : p
        )
      );
      // Also update the comments query cache if it exists
      const commentKey = queryKeys.postComments(postId);
      queryClient.setQueryData(commentKey, (old = []) => [...old, optimisticComment]);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(postsKey, context.previous);
    },
    onSuccess: ({ postId, comment }) => {
      // Replace optimistic comment with real data
      queryClient.setQueryData(postsKey, (old = []) =>
        old.map((p) =>
          String(p.id) === String(postId)
            ? {
                ...p,
                comments: (p.comments || []).map((c) =>
                  c._optimistic ? comment : c
                ),
              }
            : p
        )
      );
      const commentKey = queryKeys.postComments(postId);
      queryClient.setQueryData(commentKey, (old = []) =>
        old.map((c) => (c._optimistic ? comment : c))
      );
    },
  });
}

/**
 * useDeleteComment — deletes a comment from a post.
 * Optimistically removes it and decrements comment_count.
 */
export function useDeleteComment(page = 1) {
  const queryClient = useQueryClient();
  const postsKey = queryKeys.communityPosts(page);

  return useMutation({
    mutationFn: async ({ postId, commentId }) => {
      await httpRequest('delete', `/community/posts/${postId}/comments/${commentId}`);
      return { postId, commentId };
    },
    onMutate: async ({ postId, commentId }) => {
      await queryClient.cancelQueries({ queryKey: postsKey });
      const previous = queryClient.getQueryData(postsKey);
      queryClient.setQueryData(postsKey, (old = []) =>
        old.map((p) =>
          String(p.id) === String(postId)
            ? {
                ...p,
                comments: (p.comments || []).filter(
                  (c) => String(c.id) !== String(commentId)
                ),
                comment_count: Math.max(0, (p.comment_count || 1) - 1),
              }
            : p
        )
      );
      const commentKey = queryKeys.postComments(postId);
      queryClient.setQueryData(commentKey, (old = []) =>
        old.filter((c) => String(c.id) !== String(commentId))
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(postsKey, context.previous);
    },
  });
}

/**
 * invalidateCommunityPosts — manually refetch the feed.
 * Useful after operations that don't use optimistic updates.
 */
export function invalidateCommunityPosts(queryClient, page = 1) {
  return queryClient.invalidateQueries({ queryKey: queryKeys.communityPosts(page) });
}
